import type { Activity, Alert, AssistantContextInput } from "@cloudserver/contracts";
import { useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import type { TFunction } from "i18next";
import { Sparkles } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAssistantStatus } from "./api.js";
import { focusedTab, pageOf } from "./page.js";
import { assistant, conversationsAbout, useAssistant, type NewConversation } from "./store.js";

/*
 * «Спросить ассистента» (docs/assistant.md, "Entry points"): next to an
 * error, a failed operation, an alert, a firewall rule, a terminal's
 * selection. Each opens the chat with what it is about as a chip and a
 * first question in the composer, for the person to send or change. Shown
 * only while the chat itself is (the status the chat button read), so a
 * page never asks the API about the assistant on its own.
 */

export interface AskRequest {
  chip: AssistantContextInput | null;
  question: string;
  /** The conversation's project; the page's when absent. */
  projectId?: string | null;
  text?: NewConversation["text"];
}

/** Opens the chat about something, or null while the assistant is not on for the person. */
export function useAskAssistant(): ((request: AskRequest) => Promise<void>) | null {
  const { t } = useTranslation();
  // Never fetched here: the chat button's read of the status is reused.
  const status = useAssistantStatus(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (!status.data?.available || !status.data.enabled) return null;
  return async (request) => {
    const page = pageOf(path, focusedTab(false));
    const projectId = request.projectId !== undefined ? request.projectId : page.projectId;
    await assistant.ask(
      { ...page, projectId },
      {
        projectId,
        title: request.chip
          ? t("assistant.newAbout", { name: request.chip.label })
          : t("assistant.newConversation"),
        chip: request.chip,
        question: request.question,
        ...(request.text ? { text: request.text } : {}),
      },
    );
  };
}

/** «Спросить ассистента» about a failed operation of the activity feed. */
export function operationQuestion(
  t: TFunction,
  activity: Pick<Activity, "id" | "kind" | "operationId" | "at" | "resource" | "status">,
  kindLabel: string,
): AskRequest {
  return {
    chip: {
      kind: "operation",
      resourceId: activity.operationId ?? activity.id,
      label: `${kindLabel} ${activity.resource.name}`.slice(0, 200),
      detail: `operation ${activity.kind} on ${activity.resource.kind} "${activity.resource.name}" (id ${activity.resource.id}) ended ${activity.status} at ${activity.at}${activity.operationId ? `; operation id ${activity.operationId}` : ""}`,
    },
    question: t("assistant.ask.operation"),
  };
}

/** «Спросить ассистента» about an alert. */
export function alertQuestion(t: TFunction, alert: Alert, text: string): AskRequest {
  return {
    projectId: alert.projectId,
    chip: {
      kind: "alert",
      resourceId: alert.id,
      label: text.slice(0, 200),
      detail: [
        `alert ${alert.kind} (${alert.severity}, ${alert.status}) about "${alert.subject}"`,
        alert.serverName
          ? `on the server "${alert.serverName}" (id ${alert.serverId})`
          : "on the project",
        alert.value !== null ? `value ${alert.value}` : null,
        alert.threshold !== null ? `threshold ${alert.threshold}` : null,
        `since ${alert.startedAt}`,
        alert.resolvedAt ? `resolved ${alert.resolvedAt}` : null,
      ]
        .filter(Boolean)
        .join("; "),
    },
    question: t(alert.status === "firing" ? "assistant.ask.alertFiring" : "assistant.ask.alert"),
  };
}

/** «Спросить ассистента» about one rule of a firewall. */
export function firewallRuleQuestion(
  t: TFunction,
  firewall: { id: string; name: string },
  rule: { direction: string; protocol: string; port: string; ips: string; description: string },
  index: number,
): AskRequest {
  const what = `${rule.direction === "in" ? t("firewalls.direction.in") : t("firewalls.direction.out")} ${rule.protocol.toUpperCase()}${rule.port ? ` ${rule.port}` : ""}`;
  return {
    chip: {
      kind: "firewall_rule",
      resourceId: `${firewall.id}/${rule.direction}-${index}`,
      label: `${firewall.name}: ${what}`.slice(0, 200),
      detail: `rule ${index + 1} of the ${rule.direction === "in" ? "inbound" : "outbound"} rules of the firewall "${firewall.name}" (id ${firewall.id}): protocol ${rule.protocol}, port ${rule.port || "none"}, ${rule.direction === "in" ? "from" : "to"} ${rule.ips || "nowhere"}${rule.description ? `, described as "${rule.description}"` : ""}`,
    },
    question: t("assistant.ask.firewallRule"),
  };
}

/**
 * «Ассистент» on a server page, next to «Терминал» and «Консоль»: how many
 * conversations are about the server, and the chat filtered to them.
 */
export function ServerAssistantButton({
  projectId,
  server,
}: {
  projectId: string;
  server: { id: string; name: string };
}) {
  const { t } = useTranslation();
  const available = useAskAssistant() !== null;
  const state = useAssistant();
  useEffect(() => {
    if (available && !state.listed) void assistant.refreshList().catch(() => undefined);
  }, [available, state.listed]);
  if (!available) return null;
  const count = conversationsAbout(state.conversations, "server", server.id).length;
  const chip = { kind: "server" as const, resourceId: server.id, label: server.name };
  const label = count > 0 ? t("assistant.server.withCount", { count }) : t("assistant.server.open");
  return (
    <button
      type="button"
      onClick={() =>
        void assistant
          .openFiltered(chip, {
            projectId,
            title: t("assistant.newAbout", { name: server.name }),
            chip,
          })
          .catch(() => undefined)
      }
      aria-label={label}
      className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
    >
      <Sparkles className="size-4 text-accent" aria-hidden />
      {t("assistant.server.button")}
      {count > 0 && (
        <span className="rounded-full bg-accent-soft px-1.5 text-xs font-semibold text-accent-strong">
          {count}
        </span>
      )}
    </button>
  );
}

/** The small «Спросить ассистента» button; nothing while the assistant is off. */
export function AskAssistantButton({
  request,
  label,
  compact = false,
  className,
}: {
  request: AskRequest;
  /** What the button says; «Спросить ассистента» by default. */
  label?: string;
  /** Only the icon (the label goes to `aria-label`), for tight rows. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const ask = useAskAssistant();
  if (!ask) return null;
  const text = label ?? t("assistant.ask.button");
  return (
    <button
      type="button"
      onClick={() => void ask(request).catch(() => undefined)}
      aria-label={compact ? text : undefined}
      title={compact ? text : undefined}
      className={clsx(
        "inline-flex min-h-8 items-center gap-1.5 rounded-lg text-sm font-medium text-accent hover:bg-surface-2 hover:text-accent-strong",
        compact ? "p-1.5" : "px-2 py-1",
        className,
      )}
    >
      <Sparkles className="size-4 shrink-0" aria-hidden />
      {!compact && text}
    </button>
  );
}
